'use client';

import { useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';

import { useTranslations } from '@fshr/context/i18n-context';

import DesktopNavigation from './desktop-navigation';
import MobileNavigation from './mobile-navigation';
import { Empty } from '@fshr/microservices/compiled/profile.v1';
import { getConfig } from '@fshr/shared-react';
import type { NavigationLink } from '@micro-apps/env';

export function compareLinkToPath(link: string, pathname: string) {
	try {
		const { STATIC_SITE_URL } = getConfig();
		if (
			!link ||
			!(link.startsWith('/') || (STATIC_SITE_URL && link.startsWith(STATIC_SITE_URL as string)))
		) {
			return false;
		}
		const normalizedLink = STATIC_SITE_URL
			? new URL(link, STATIC_SITE_URL as string).pathname
			: link;
		return normalizedLink === pathname;
	} catch {
		return false;
	}
}

interface NavigationProps {
	isSignedIn: boolean;
	isPersisted: boolean;
	isFullyAuthenticated: boolean;
	avatar?: string;
	name?: string;
	initials?: string;
	advisorId?: string;
	hasUpcomingTrips?: boolean;
	hasMembership?: boolean;
	hasOwnedProperties?: boolean;
	isEmployee?: boolean;
	signOut: () => Promise<Empty>;
	navLinks: NavigationLink[];
	isLoading: boolean;
	topBarLinks?: NavigationLink[];
	ppMode?: boolean;
	moreLinksFudgeFactor?: number;
}

export default function Navigation({
	isSignedIn,
	isPersisted,
	isFullyAuthenticated,
	avatar,
	name,
	initials,
	advisorId,
	hasUpcomingTrips,
	hasMembership,
	hasOwnedProperties,
	isEmployee,
	signOut,
	navLinks,
	isLoading,
	topBarLinks,
	ppMode,
	moreLinksFudgeFactor,
}: NavigationProps) {
	const t = useTranslations();

	// TODO:
	// isMounted is used to prevent SSR hydration error,
	// supposedly useMediaQuery should handle this,
	// but seems it doesn't
	const [isMounted, setIsMounted] = useState(false);
	const isDesktop = useMediaQuery({ minWidth: 1101 });

	useEffect(() => {
		setIsMounted(true);
	}, []);

	if (!isMounted) {
		return null;
	}

	if (!isLoading && isDesktop) {
		return (
			<DesktopNavigation
				navLinks={navLinks}
				content={{ moreLabel: t('more...') }}
				fudgeFactor={moreLinksFudgeFactor}
			/>
		);
	}

	return (
		<MobileNavigation
			navLinks={navLinks}
			isSignedIn={isSignedIn}
			isPersisted={isPersisted}
			isFullyAuthenticated={isFullyAuthenticated}
			avatar={avatar}
			name={name}
			initials={initials}
			advisorId={advisorId}
			hasUpcomingTrips={hasUpcomingTrips}
			hasMembership={hasMembership}
			hasOwnedProperties={hasOwnedProperties}
			isEmployee={isEmployee}
			signOut={signOut}
			topBarLinks={topBarLinks}
			ppMode={ppMode}
		/>
	);
}
